import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Download, FileText, Clock, CheckCircle, AlertCircle, FileEdit, FileCode, Pencil, Send, Loader2, RotateCcw, XCircle, Trash2, Mail } from "lucide-react";
import { generateInvoicePdf } from "@/lib/generateInvoicePdf";
import { generateInvoiceXml, validateInvoiceForXml, downloadXml } from "@/lib/generateInvoiceXml";
import { uploadInvoiceFiles } from "@/lib/uploadInvoiceFiles";
import AppHeader from "@/components/AppHeader";
import AppFooter from "@/components/AppFooter";
import AccountantSendDialog from "@/components/AccountantSendDialog";

const statusConfig: Record<string, { label: string; icon: any; variant: "default" | "secondary" | "destructive" | "outline" | "success" | "warning" | "info" }> = {
  draft: { label: "Ciornă", icon: FileEdit, variant: "secondary" },
  pending: { label: "În așteptare", icon: Clock, variant: "warning" },
  validated: { label: "Validată", icon: CheckCircle, variant: "success" },
  error: { label: "Eroare", icon: AlertCircle, variant: "destructive" },
};

const formatRON = (v: number) =>
  v.toLocaleString("ro-RO", { style: "currency", currency: "RON" });

const InvoiceDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, loading: authLoading } = useAuth();
  const [sending, setSending] = useState(false);
  const [acctDialogOpen, setAcctDialogOpen] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) navigate("/auth");
  }, [user, authLoading, navigate]);

  const { data: invoice, isLoading } = useQuery({
    queryKey: ["invoice", id],
    enabled: !!user && !!id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("invoices")
        .select("*")
        .eq("id", id!)
        .single();
      if (error) throw error;
      return data;
    },
  });

  const { data: items } = useQuery({
    queryKey: ["invoice-items", id],
    enabled: !!user && !!id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("invoice_items")
        .select("*")
        .eq("invoice_id", id!)
        .order("sort_order");
      if (error) throw error;
      return data;
    },
  });

  // Lazy backfill: if invoice was created before pre-save was added,
  // generate and store its PDF/XML now so accountant export works later.
  useEffect(() => {
    if (!invoice || !items || !user) return;
    if ((invoice as any).pdf_path && (invoice as any).xml_path) return;
    uploadInvoiceFiles({
      id: invoice.id,
      user_id: user.id,
      invoice_number: invoice.invoice_number,
      issue_date: invoice.issue_date,
      due_date: invoice.due_date,
      seller_name: invoice.seller_name,
      seller_cui: invoice.seller_cui,
      seller_address: invoice.seller_address,
      seller_city: invoice.seller_city,
      seller_county: invoice.seller_county,
      buyer_name: invoice.buyer_name,
      buyer_cui: invoice.buyer_cui,
      buyer_address: invoice.buyer_address,
      buyer_city: invoice.buyer_city,
      buyer_county: invoice.buyer_county,
      subtotal: Number(invoice.subtotal) || 0,
      vat_total: Number(invoice.vat_total) || 0,
      total: Number(invoice.total) || 0,
      items: items.map((i: any) => ({
        item_name: i.item_name,
        quantity: Number(i.quantity),
        unit_price: Number(i.unit_price),
        vat_rate: Number(i.vat_rate),
        unit_code: i.unit_code || "C62",
      })),
    }).catch(() => { /* silent */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice?.id, items?.length]);

  // Lookup buyer CUI for TVA/e-Factura badges
  const buyerCui = invoice?.buyer_cui?.trim();
  const hasBuyerCui = !!buyerCui && buyerCui.replace(/^RO/i, "").length >= 2;
  const { data: buyerInfo } = useQuery({
    queryKey: ["cui-info", buyerCui],
    enabled: hasBuyerCui,
    staleTime: 1000 * 60 * 30,
    retry: false,
    queryFn: async () => {
      try {
        const { data, error } = await supabase.functions.invoke("cui-lookup", {
          body: { cui: buyerCui },
        });
        if (error || data?.error) return null;
        return data as { vat_registered: boolean; e_factura: boolean };
      } catch {
        return null;
      }
    },
  });

  const handleDownload = () => {
    if (!invoice || !items) return;
    try {
      const doc = generateInvoicePdf({
        invoice_number: invoice.invoice_number,
        issue_date: invoice.issue_date,
        due_date: invoice.due_date,
        seller_name: invoice.seller_name,
        seller_cui: invoice.seller_cui,
        seller_address: invoice.seller_address,
        seller_city: invoice.seller_city,
        seller_county: invoice.seller_county,
        buyer_name: invoice.buyer_name,
        buyer_cui: invoice.buyer_cui,
        buyer_address: invoice.buyer_address,
        buyer_city: invoice.buyer_city,
        buyer_county: invoice.buyer_county,
        subtotal: Number(invoice.subtotal),
        vat_total: Number(invoice.vat_total),
        total: Number(invoice.total),
        items: items.map((i) => ({
          item_name: i.item_name,
          quantity: Number(i.quantity),
          unit_price: Number(i.unit_price),
          vat_rate: Number(i.vat_rate),
          unit_code: i.unit_code || "C62",
        })),
      });
      const filename = `factura-${invoice.invoice_number || invoice.id.slice(0, 8)}.pdf`;
      doc.save(filename);
      toast.success("PDF descărcat!");
    } catch {
      toast.error("Eroare la generarea PDF-ului");
    }
  };

  const handleDownloadXml = () => {
    if (!invoice || !items) return;

    const inv = invoice;
    const xmlData = {
      invoice_number: inv.invoice_number,
      issue_date: inv.issue_date,
      due_date: inv.due_date,
      seller_name: inv.seller_name,
      seller_cui: inv.seller_cui,
      seller_address: inv.seller_address,
      seller_city: inv.seller_city || null,
      seller_county: inv.seller_county || null,
      buyer_name: inv.buyer_name,
      buyer_cui: inv.buyer_cui,
      buyer_address: inv.buyer_address,
      buyer_city: inv.buyer_city || null,
      buyer_county: inv.buyer_county || null,
      subtotal: Number(inv.subtotal),
      vat_total: Number(inv.vat_total),
      total: Number(inv.total),
      items: items.map((i: any) => ({
        item_name: i.item_name,
        quantity: Number(i.quantity),
        unit_price: Number(i.unit_price),
        vat_rate: Number(i.vat_rate),
        unit_code: i.unit_code || "C62",
      })),
    };

    const errors = validateInvoiceForXml(xmlData);
    if (errors.length > 0) {
      toast.error(
        `Câmpuri lipsă pentru XML e-Factura:\n• ${errors.join("\n• ")}`,
        { duration: 8000 }
      );
      return;
    }

    try {
      const xml = generateInvoiceXml(xmlData);
      const filename = `efactura-${inv.invoice_number || inv.id.slice(0, 8)}.xml`;
      downloadXml(xml, filename);
      toast.success("XML e-Factura descărcat!");
    } catch {
      toast.error("Eroare la generarea XML-ului");
    }
  };

  const handleResendToAnaf = async () => {
    if (!invoice || !items || !id) return;

    const inv = invoice;
    // Validate XML-readiness first
    const xmlData = {
      invoice_number: inv.invoice_number,
      issue_date: inv.issue_date,
      due_date: inv.due_date,
      seller_name: inv.seller_name,
      seller_cui: inv.seller_cui,
      seller_address: inv.seller_address,
      seller_city: inv.seller_city || null,
      seller_county: inv.seller_county || null,
      buyer_name: inv.buyer_name,
      buyer_cui: inv.buyer_cui,
      buyer_address: inv.buyer_address,
      buyer_city: inv.buyer_city || null,
      buyer_county: inv.buyer_county || null,
      subtotal: Number(inv.subtotal),
      vat_total: Number(inv.vat_total),
      total: Number(inv.total),
      items: items.map((i: any) => ({
        item_name: i.item_name,
        quantity: Number(i.quantity),
        unit_price: Number(i.unit_price),
        vat_rate: Number(i.vat_rate),
        unit_code: i.unit_code || "C62",
      })),
    };

    const errors = validateInvoiceForXml(xmlData);
    if (errors.length > 0) {
      toast.error(
        `Câmpuri lipsă — editează factura mai întâi:\n• ${errors.join("\n• ")}`,
        { duration: 8000 }
      );
      return;
    }

    setSending(true);
    try {
      const { error } = await supabase
        .from("invoices")
        .update({ status: "pending" } as any)
        .eq("id", id);
      if (error) throw error;
      queryClient.invalidateQueries({ queryKey: ["invoice", id] });
      toast.success("Factura a fost trimisă la ANAF (simulare)!");
    } catch {
      toast.error("Eroare la trimiterea către ANAF");
    } finally {
      setSending(false);
    }
  };

  const handleStorneaza = async () => {
    if (!invoice || !items || !user) return;
    try {
      const today = new Date().toISOString().split("T")[0];
      const stornoNumber = `STORNO-${invoice.invoice_number || invoice.id.slice(0, 8)}`;

      // Create reversal invoice with negative amounts
      const { data: newInvoice, error } = await supabase
        .from("invoices")
        .insert({
          user_id: user.id,
          invoice_number: stornoNumber,
          status: "draft" as const,
          seller_name: invoice.seller_name,
          seller_cui: invoice.seller_cui,
          seller_address: invoice.seller_address,
          seller_city: invoice.seller_city,
          seller_county: invoice.seller_county,
          buyer_name: invoice.buyer_name,
          buyer_cui: invoice.buyer_cui,
          buyer_address: invoice.buyer_address,
          buyer_city: invoice.buyer_city,
          buyer_county: invoice.buyer_county,
          subtotal: -Math.abs(Number(invoice.subtotal)),
          vat_total: -Math.abs(Number(invoice.vat_total)),
          total: -Math.abs(Number(invoice.total)),
          issue_date: today,
          due_date: null,
        } as any)
        .select()
        .single();

      if (error) throw error;

      // Create reversal line items with negative quantities
      const lineItems = items.map((i, idx) => ({
        invoice_id: newInvoice.id,
        item_name: i.item_name,
        quantity: -Math.abs(Number(i.quantity)),
        unit_price: Number(i.unit_price),
        vat_rate: Number(i.vat_rate),
        unit_code: i.unit_code || "C62",
        sort_order: idx,
      } as any));

      if (lineItems.length) {
        const { error: itemsError } = await supabase.from("invoice_items").insert(lineItems);
        if (itemsError) throw itemsError;
      }

      toast.success("Factură de stornare creată! Verifică datele și trimite la ANAF.");
      navigate(`/invoice/${newInvoice.id}/edit`);
    } catch (err: any) {
      toast.error(err.message || "Eroare la crearea stornării");
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    try {
      // Delete items first, then invoice
      await supabase.from("invoice_items").delete().eq("invoice_id", id);
      const { error } = await supabase.from("invoices").delete().eq("id", id);
      if (error) throw error;
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
      toast.success("Factura a fost ștearsă!");
      navigate("/dashboard");
    } catch (err: any) {
      toast.error(err.message || "Eroare la ștergerea facturii");
    }
  };

  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4">
        <p className="text-muted-foreground">Factura nu a fost găsită</p>
        <Button variant="outline" onClick={() => navigate("/dashboard")}>Înapoi</Button>
      </div>
    );
  }

  const sc = statusConfig[invoice.status] || statusConfig.draft;
  const Icon = sc.icon;

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />

      <TooltipProvider delayDuration={300}>
        <main className="container py-6 max-w-xl space-y-6">
          {/* Disclaimer */}
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-2.5 flex items-center gap-2 text-xs text-muted-foreground">
            <AlertCircle className="h-4 w-4 text-destructive shrink-0" />
            <div className="flex flex-col">
              <span><strong className="text-foreground">Copie de lucru</strong> — acest document nu are valoare fiscală oficială.</span>
              <span>Documentul oficial se află în sistemul SPV al ANAF.</span>
            </div>
          </div>
          {/* Status & Number */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-heading text-xl font-bold">
                {invoice.invoice_number || "Fără număr"}
              </h2>
              <p className="text-sm text-muted-foreground">
                {new Date(invoice.issue_date).toLocaleDateString("ro-RO")}
                {invoice.due_date && ` · Scadență: ${new Date(invoice.due_date).toLocaleDateString("ro-RO")}`}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {(invoice.status === "draft" || invoice.status === "error") && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="outline" size="sm" onClick={() => navigate(`/invoice/${id}/edit`)}>
                      <Pencil className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Editează factura</TooltipContent>
                </Tooltip>
              )}
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <Badge variant={sc.variant} className="gap-1">
                      <Icon className="h-3 w-3" />
                      {sc.label}
                    </Badge>
                  </div>
                </TooltipTrigger>
                <TooltipContent>Statusul curent al facturii</TooltipContent>
              </Tooltip>
            </div>
          </div>

          {/* Seller / Buyer */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="rounded-xl border border-border bg-card p-4 space-y-1">
              <p className="text-xs text-muted-foreground font-semibold uppercase">Furnizor</p>
              <p className="font-medium">{invoice.seller_name || "—"}</p>
              {invoice.seller_cui && <p className="text-sm text-muted-foreground">CUI: {invoice.seller_cui}</p>}
              {invoice.seller_address && <p className="text-sm text-muted-foreground">{invoice.seller_address}</p>}
              {(invoice.seller_city || invoice.seller_county) && (
                <p className="text-sm text-muted-foreground">
                  {[invoice.seller_city, invoice.seller_county].filter(Boolean).join(", ")}
                </p>
              )}
            </div>
            <div className="rounded-xl border border-border bg-card p-4 space-y-2">
              <p className="text-xs text-muted-foreground font-semibold uppercase">Client</p>
              <p className="font-medium">{invoice.buyer_name || "—"}</p>
              {invoice.buyer_cui && <p className="text-sm text-muted-foreground">CUI: {invoice.buyer_cui}</p>}
              {invoice.buyer_address && <p className="text-sm text-muted-foreground">{invoice.buyer_address}</p>}
              {(invoice.buyer_city || invoice.buyer_county) && (
                <p className="text-sm text-muted-foreground">
                  {[invoice.buyer_city, invoice.buyer_county].filter(Boolean).join(", ")}
                </p>
              )}
              {buyerInfo && (
                <div className="flex gap-2 pt-1 flex-wrap">
                  <Badge variant={buyerInfo.vat_registered ? "default" : "secondary"} className="gap-1 text-xs">
                    {buyerInfo.vat_registered ? <CheckCircle className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                    {buyerInfo.vat_registered ? "Plătitor TVA" : "Neplătitor TVA"}
                  </Badge>
                  <Badge variant={buyerInfo.e_factura ? "default" : "outline"} className="gap-1 text-xs">
                    {buyerInfo.e_factura ? <CheckCircle className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                    {buyerInfo.e_factura ? "e-Factura activ" : "e-Factura inactiv"}
                  </Badge>
                </div>
              )}
            </div>
          </div>

          {/* Line Items */}
          <div className="rounded-xl border border-border overflow-hidden">
            <div className="bg-card px-4 py-3 border-b border-border">
              <h3 className="font-heading font-semibold text-sm flex items-center gap-2">
                <FileText className="h-4 w-4 text-primary" /> Produse / Servicii
              </h3>
            </div>
            {items?.length ? (
              <div className="divide-y divide-border">
                {items.map((item, idx) => (
                  <div key={item.id} className="px-4 py-3 flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm">{idx + 1}. {item.item_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {Number(item.quantity)} × {Number(item.unit_price).toLocaleString("ro-RO")} RON · TVA {Number(item.vat_rate)}%
                      </p>
                    </div>
                    <span className="text-sm font-medium whitespace-nowrap">
                      {formatRON(Number(item.quantity) * Number(item.unit_price))}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground px-4 py-6 text-center">Niciun produs</p>
            )}
          </div>

          {/* Totals */}
          <div className="rounded-xl border border-primary/30 bg-primary/5 p-5 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Subtotal</span>
              <span>{formatRON(Number(invoice.subtotal))}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">TVA</span>
              <span>{formatRON(Number(invoice.vat_total))}</span>
            </div>
            <div className="border-t border-border pt-2 flex justify-between font-heading font-bold text-lg">
              <span>Total</span>
              <span className="text-primary">{formatRON(Number(invoice.total))}</span>
            </div>
          </div>

          {/* Download CTAs */}
          <div className="flex gap-3">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" className="flex-1 h-12 font-semibold transition-all duration-200 hover:scale-105 hover:shadow-lg active:scale-95" onClick={handleDownload}>
                  <Download className="h-5 w-5 mr-2" />
                  Descarcă PDF
                </Button>
              </TooltipTrigger>
              <TooltipContent>Descarcă factura în format PDF</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" className="flex-1 h-12 font-semibold transition-all duration-200 hover:scale-105 hover:shadow-lg active:scale-95" onClick={handleDownloadXml}>
                  <FileCode className="h-5 w-5 mr-2" />
                  Descarcă XML
                </Button>
              </TooltipTrigger>
              <TooltipContent>Descarcă XML e-Factura (format RO_CIUS)</TooltipContent>
            </Tooltip>
          </div>

          {/* Send to accountant — for any invoice */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                className="w-full h-12 font-semibold transition-all duration-200 hover:scale-[1.02] active:scale-95"
                onClick={() => setAcctDialogOpen(true)}
              >
                <Mail className="h-5 w-5 mr-2" />
                Trimite la contabil
              </Button>
            </TooltipTrigger>
            <TooltipContent>Trimite această factură (PDF + XML) către contabilul tău</TooltipContent>
          </Tooltip>

          {/* Re-send to ANAF — only for draft/error */}
          {(invoice.status === "draft" || invoice.status === "error") && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  className="w-full h-12 font-semibold transition-all duration-200 hover:scale-105 hover:shadow-lg active:scale-95"
                  onClick={handleResendToAnaf}
                  disabled={sending}
                >
                  {sending ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <Send className="h-5 w-5 mr-2" />}
                  {invoice.status === "error" ? "Re-trimite la ANAF" : "Trimite la ANAF"}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {invoice.status === "error"
                  ? "Re-trimite factura corectată la ANAF"
                  : "Trimite factura la ANAF (simulare)"}
              </TooltipContent>
            </Tooltip>
          )}

          {/* Stornare — only for validated/pending invoices */}
          {(invoice.status === "validated" || invoice.status === "pending") && (
            <AlertDialog>
              <Tooltip>
                <TooltipTrigger asChild>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full h-12 font-semibold text-destructive border-destructive/30 hover:bg-destructive hover:text-destructive-foreground hover:border-destructive transition-all duration-200 hover:scale-[1.02] active:scale-95"
                    >
                      <RotateCcw className="h-5 w-5 mr-2" />
                      Stornează Factura
                    </Button>
                  </AlertDialogTrigger>
                </TooltipTrigger>
                <TooltipContent>Creează automat o factură de stornare (cu sume negative) pe baza acestei facturi</TooltipContent>
              </Tooltip>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Confirmă stornarea</AlertDialogTitle>
                  <AlertDialogDescription>
                    Se va crea o factură nouă cu sume negative care anulează factura {invoice.invoice_number || "curentă"}. Acțiunea nu poate fi anulată. Continui?
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Renunță</AlertDialogCancel>
                  <AlertDialogAction onClick={handleStorneaza} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                    Da, stornează
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}

          {/* Delete — only for drafts */}
          {invoice.status === "draft" && (
            <AlertDialog>
              <Tooltip>
                <TooltipTrigger asChild>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full h-12 font-semibold text-destructive border-destructive/30 hover:bg-destructive hover:text-destructive-foreground hover:border-destructive transition-all duration-200 hover:scale-[1.02] active:scale-95"
                    >
                      <Trash2 className="h-5 w-5 mr-2" />
                      Șterge Factura
                    </Button>
                  </AlertDialogTrigger>
                </TooltipTrigger>
                <TooltipContent>Șterge definitiv această factură</TooltipContent>
              </Tooltip>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Șterge factura?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Factura {invoice.invoice_number || "fără număr"} va fi ștearsă definitiv. Acțiunea nu poate fi anulată.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Renunță</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                    Da, șterge
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </main>
      </TooltipProvider>
      <AccountantSendDialog
        open={acctDialogOpen}
        onOpenChange={setAcctDialogOpen}
        invoiceIds={id ? [id] : []}
        contextLabel={`Factura ${invoice?.invoice_number || (id?.slice(0, 8) ?? "")}`}
      />
      <AppFooter />
    </div>
  );
};

export default InvoiceDetail;
